import { IChannelNotificationResponse } from "@wingspanhq/notifications/dist/lib/interfaces/api/notifications";
import React from "react";
import { useHistory } from "react-router-dom";
import { BackgroundCheckRequestContractor } from "./templates/BackgroundCheckRequestContractor";
import { BankingFailed } from "./templates/BankingFailed";
import { BankingPending } from "./templates/BankingPending";
import { BankingUpdateRequired } from "./templates/BankingUpdateRequired";
import { BusinessInfo } from "./templates/BusinessInfo";
import { Collaborators } from "./templates/Collaborators";
import { FileSharingRequestContractor } from "./templates/FileSharingRequestContractor";
import { IdentityVerificationRequestContractor } from "./templates/IdentityVerificationRequestContractor";
import { InsuranceCoverageMonitoringContractor } from "./templates/InsuranceCoverageMonitoringContractor";
import { PayoutMethod } from "./templates/PayoutMethod";
import { PayoutsInfo } from "./templates/PayoutsInfo";
import { Payroll } from "./templates/Payroll";
import { RequirementPendingReviewClient } from "./templates/RequirementPendingReviewClient";
import { SignatureClient } from "./templates/SignatureClient";
import { SignatureCollaborator } from "./templates/SignatureCollaborator";
import { TINVerificationPending } from "./templates/TINVerificationPending";
import { TaxFailed } from "./templates/TaxFailed";
import { TaxPending } from "./templates/TaxPending";
import { TaxUpdateRequired } from "./templates/TaxUpdateRequired";
import { TaxVerification } from "./templates/TaxVerification";
import { W8BENCertification } from "./templates/W8BENCertification";
import { W9Certification } from "./templates/W9Certification";
import { W9Consent } from "./templates/W9Consent";
import { W9InfoCollaborator } from "./templates/W9InfoCollaborator";
import { WorkLogSubmittedForReviewPayer } from "./templates/WorkLogSubmittedForReviewPayer";

export enum NotificationHandleType {
  W9InfoCollaborator = "W9InfoCollaborator",
  // W9InfoUpdate = "W9InfoUpdate",
  W9Consent = "W9Consent",
  W9Certification = "W9Certification",
  W8BENCertification = "W8BENCertification",
  TINVerification = "TINVerification",
  TINVerificationPending = "TINVerificationPending",
  PayoutsInfo = "PayoutsInfo",
  // PayoutsInfoUpdate = "PayoutsInfoUpdate",
  SignatureCollaborator = "SignatureCollaborator",
  FileSharingRequestContractor = "FileSharingRequestContractor",
  BackgroundCheckRequestContractor = "BackgroundCheckRequestContractor",
  IdentityVerificationRequestContractor = "IdentityVerificationRequestContractor",
  RequirementPendingReviewClient = "RequirementPendingReviewClient",
  BusinessInfo = "BusinessInfo",
  Collaborators = "Collaborators",
  Payroll = "Payroll",
  SignatureClient = "SignatureClient",
  PayoutMethod = "PayoutMethod",
  WorkLogSubmittedForReviewPayer = "WorkLogSubmittedForReviewPayer",

  InsuranceCoverageMonitoringContractor = "InsuranceCoverageMonitoringContractor",

  TaxPending = "TaxPending",
  TaxVerification = "TaxVerification",
  TaxVerified = "TaxVerified",
  TaxUpdateRequired = "TaxUpdateRequired",
  TaxFailed = "TaxFailed",

  BankingPending = "BankingPending",
  BankingVerified = "BankingVerified",
  BankingUpdateRequired = "BankingUpdateRequired",
  BankingFailed = "BankingFailed",

  RequirementsApprovedPayee = "RequirementsApprovedPayee",
  RequirementsReRequestedPayee = "RequirementsReRequestedPayee",
  RequirementsRejectedPayee = "RequirementsRejectedPayee",
  RequirementsCompletePayee = "RequirementsCompletePayee",
  RequirementsAwaitingReviewPayee = "RequirementsAwaitingReviewPayee"
}

export type NotificationProps<C> = Omit<
  IChannelNotificationResponse,
  "context"
> & {
  context: C;
  group: (Omit<IChannelNotificationResponse, "context"> & {
    context: C;
  })[];
};

export type NotificationTemplate<C = undefined> = {
  isPending?: boolean;
  renderTitle: (props: NotificationProps<C>) => string;
  renderBody: (
    props: NotificationProps<C>,
    history: ReturnType<typeof useHistory>
  ) => React.ReactNode;
  Footer?: React.FC<NotificationProps<C>>;
  Header?: React.FC<NotificationProps<C>>;
  buttonText?: (props: NotificationProps<C>) => string;
  buttonAction?: (
    props: NotificationProps<C>,
    history: ReturnType<typeof useHistory>
  ) => () => void;
  buttonActionAsync?: (
    props: NotificationProps<C>,
    history: ReturnType<typeof useHistory>
  ) => () => Promise<void>;
};

export const NotificationTemplates = {
  [NotificationHandleType.BusinessInfo]: BusinessInfo,
  [NotificationHandleType.Collaborators]: Collaborators,
  [NotificationHandleType.Payroll]: Payroll,
  [NotificationHandleType.SignatureCollaborator]: SignatureCollaborator,
  [NotificationHandleType.FileSharingRequestContractor]:
    FileSharingRequestContractor,
  [NotificationHandleType.BackgroundCheckRequestContractor]:
    BackgroundCheckRequestContractor,
  [NotificationHandleType.IdentityVerificationRequestContractor]:
    IdentityVerificationRequestContractor,
  [NotificationHandleType.InsuranceCoverageMonitoringContractor]:
    InsuranceCoverageMonitoringContractor,
  [NotificationHandleType.SignatureClient]: SignatureClient,
  [NotificationHandleType.PayoutsInfo]: PayoutsInfo,
  [NotificationHandleType.TINVerificationPending]: TINVerificationPending,
  [NotificationHandleType.W8BENCertification]: W8BENCertification,
  [NotificationHandleType.W9Certification]: W9Certification,
  [NotificationHandleType.W9Consent]: W9Consent,
  [NotificationHandleType.W9InfoCollaborator]: W9InfoCollaborator,
  [NotificationHandleType.PayoutMethod]: PayoutMethod,

  [NotificationHandleType.TaxVerification]: TaxVerification,
  [NotificationHandleType.TaxPending]: TaxPending,
  [NotificationHandleType.TaxUpdateRequired]: TaxUpdateRequired,
  [NotificationHandleType.TaxFailed]: TaxFailed,
  [NotificationHandleType.BankingPending]: BankingPending,
  [NotificationHandleType.BankingUpdateRequired]: BankingUpdateRequired,
  [NotificationHandleType.BankingFailed]: BankingFailed,

  [NotificationHandleType.RequirementPendingReviewClient]:
    RequirementPendingReviewClient,

  [NotificationHandleType.WorkLogSubmittedForReviewPayer]:
    WorkLogSubmittedForReviewPayer
};

/**
 * Utility type to extract the context type C from NotificationTemplate<C>.
 */
type ExtractTemplateContext<T> =
  T extends NotificationTemplate<infer C> ? C : never;

/**
 * Maps each notification handle type to its specific context type.
 * Derived automatically from NotificationTemplates to avoid duplication.
 */
export type NotificationContextMap = {
  [K in keyof typeof NotificationTemplates]: ExtractTemplateContext<
    (typeof NotificationTemplates)[K]
  >;
};

/**
 * Union of all possible notification context types.
 * Used when the specific handle type is not known at compile time.
 */
export type AnyNotificationContext =
  NotificationContextMap[keyof NotificationContextMap];

/**
 * Union type of all possible notification props.
 * Each member is a discriminated type with its handle correlating to its context.
 */
export type NotificationTemplateParameters = {
  [K in keyof typeof NotificationTemplates]: Parameters<
    (typeof NotificationTemplates)[K]["renderTitle"]
  >[0];
}[keyof typeof NotificationTemplates];

/**
 * Gets a notification template by handle, typed for polymorphic dispatch.
 * Returns undefined if the handle doesn't match any known template.
 *
 * This function provides a type-safe bridge between the strongly-typed
 * individual templates and the runtime dispatch scenario where the
 * specific handle type is not known at compile time.
 */
export function getNotificationTemplate(
  handle: string | undefined
): NotificationTemplate<AnyNotificationContext> | undefined {
  if (!handle || !(handle in NotificationTemplates)) {
    return undefined;
  }

  return NotificationTemplates[
    handle as keyof typeof NotificationTemplates
  ] as NotificationTemplate<AnyNotificationContext>;
}

export interface WSNotificationResponse<
  C extends AnyNotificationContext = AnyNotificationContext
> extends Omit<IChannelNotificationResponse, "context"> {
  context: C;
}
